import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { Component } from "@theme/component";
import { observerElement, onIdleAfterPageLoad } from '@theme/utilities';
import { RecentlyViewed } from '@theme/storage';

class RecentlyViewedComponent extends Component {
  /**
   * Intersection observer to lazy load the component when it enters the viewport.
   * @type {IntersectionObserver}
   */
  #intersectionObserver = null;

  /** @type {number} Number of products currently loaded */
  #loadedCount = 0;

  /** @type {boolean} Whether button event listener has been initialized */
  #buttonInitialized = false;

  /** @type {boolean} Whether render has been triggered */
  #hasRendered = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.#shouldLazyLoad()) {
      this.#intersectionObserver = observerElement({
        element: this,
        callback: () => this.#load(),
        threshold: '1000'
      });
    } else {
      onIdleAfterPageLoad(() => this.#load());
    }
  }

  /**
   * Clears all recently viewed products from storage
   * Shows empty state and hides product list
   */
  clearAll = () => {
    RecentlyViewed.clearProducts();
    this.querySelector('.recently-viewed__empty').removeAttribute('hidden');
    this.querySelector('.product-card-list')?.setAttribute('hidden', '');
    this.#showEmptyControls();
    this.#loadedCount = 0;
  }

  /**
   * The load more button, or null when the section doesn't paginate.
   * @returns {HTMLElement|null}
   */
  get #loadMoreWrapper() {
    return this.querySelector('[ref="loadMoreBtn"]')?.closest('.recently-viewed__load-more-button') ?? null;
  }

  /**
   * Shows or hides the load more button.
   * @param {boolean} visible
   */
  #setLoadMoreVisible(visible) {
    const wrapper = this.#loadMoreWrapper;
    if (wrapper) wrapper.style.display = visible ? '' : 'none';
  }

  /**
   * Hides the header and controls that only make sense while there are
   * products to act on, leaving just the empty state.
   */
  #showEmptyControls() {
    this.#setLoadMoreVisible(false);
    const header = this.querySelector('.recently-viewed__header');
    if (header) header.style.display = 'none';
  }

  /**
   * Shows empty state or removes component if empty state element doesn't exist.
   * When data-empty-behavior="remove" is set, always removes the component
   * instead of showing an empty state.
   */
  #handleEmptyState() {
    if (this.emptyBehavior === 'remove') {
      this.remove();
      return;
    }
    if (this.querySelector('.recently-viewed__empty')) {
      this.querySelector('.recently-viewed__empty').removeAttribute('hidden');
      this.refs.recentlyViewedList.classList.remove('hidden');
      this.#showEmptyControls();
    } else {
      this.remove();
    }
  }

  /**
   * Renders recently viewed products from localStorage
   * Fetches product data via Section Rendering API and morphs into DOM
   * @param {number} [offset=1] - Starting offset for pagination
   */
  #loadRecentlyViewed = async (offset = 1) => {
    const limit = this.productsLimit;
    const viewedProducts = RecentlyViewed.getProducts(limit, offset);
    if (viewedProducts.length === 0) {
      this.#handleEmptyState();
      return;
    }

    const productViewedList = this.refs.recentlyViewedList;
    if (!productViewedList) {
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    // Skip the section cache in the theme editor so block/heading setting
    // changes (which re-fire shopify:section:load with the same fetch URL)
    // render fresh instead of morphing the stale cached HTML back in.
    return sectionRenderer.getSectionHTML(this.sectionId, !Shopify.designMode, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        const parseHtml = new DOMParser().parseFromString(resultsMarkup, 'text/html');
        const results = parseHtml.querySelector('[ref="recentlyViewedList"]');
        if (!results) {
          this.#handleEmptyState();
          return;
        }

        const pageSize = this.#pageSizeOf(results) ?? viewedProducts.length;

        if (offset === 1) {
          // First load: replace content
          morph(productViewedList, results);
          this.#loadedCount = pageSize;
        } else {
          // Load more: append new products
          const newContainer = results.querySelector('.product-card-list__inner');
          const existingContainer = productViewedList.querySelector('.product-card-list__inner');

          if (!existingContainer || !newContainer) {
            this.#handleEmptyState();
            return;
          }

          // Append all children from newContainer to existingContainer
          Array.from(newContainer.children).forEach(child => {
            existingContainer.appendChild(child);
          });
          this.#loadedCount += pageSize;
        }

        this.classList.remove('hidden');
        productViewedList.classList.remove('hidden');

        // Always update button to handle show/hide, but it won't recreate if already exists
        this.#updateLoadMoreButton();
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  }

  /**
   * Reads how many ids the rendered product list consumed, which is what the
   * next offset must advance by.
   * @param {Element} results - The freshly rendered list markup
   * @returns {number|null} The page size, or null when the list didn't declare one
   */
  #pageSizeOf(results) {
    const pageSize = parseInt(results.querySelector('[data-page-size]')?.getAttribute('data-page-size') ?? '');
    return Number.isInteger(pageSize) && pageSize > 0 ? pageSize : null;
  }

  /**
   * Updates or shows/hides the Load More button based on available products.
   * Only works if data-pagination attribute is present.
   */
  #updateLoadMoreButton() {
    const loadMoreBtn = this.querySelector('[ref="loadMoreBtn"]');

    if (!loadMoreBtn || !this.#loadMoreWrapper) {
      return;
    }

    // Initialize button event listener once
    if (!this.#buttonInitialized) {
      loadMoreBtn.addEventListener('click', () => this.#loadMore());
      this.#buttonInitialized = true;
    }

    this.#setLoadMoreVisible(this.#loadedCount < RecentlyViewed.getTotalCount());
  }


  /**
   * Loads more products when Load More button is clicked.
   */
  #loadMore = async () => {
    const loadMoreBtn = this.querySelector('[ref="loadMoreBtn"]');
    if (!loadMoreBtn) return;

    loadMoreBtn.classList.add('btn--loading');
    loadMoreBtn.disabled = true;

    const nextOffset = this.#loadedCount + 1;
    await this.#loadRecentlyViewed(nextOffset);

    loadMoreBtn.classList.remove('btn--loading');
    loadMoreBtn.disabled = false;

  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  get productsLimit() {
    const { productsLimit } = this.dataset || {};
    if (!productsLimit) return 6;
    return parseInt(productsLimit);
  }

  /**
   * How to handle the empty state. 'remove' removes the component entirely
   * when there are no products; otherwise the empty state is shown.
   * @returns {string|undefined}
   */
  get emptyBehavior() {
    return this.dataset.emptyBehavior;
  }

  /**
   * Renders component after document is ready
   * Used for lazy loading scenarios
   */
  #load = () => {
    if (RecentlyViewed.getProducts(1).length === 0) {
      this.#handleEmptyState();
      return;
    }
    this.#hasRendered = true;
    this.#loadRecentlyViewed();
  };

  /**
   * Checks if component should lazy load based on data-loading attribute
   * @returns {boolean} True if lazy loading is enabled
   */
  #shouldLazyLoad() {
    return this.getAttribute('data-loading') === 'lazy';
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewedComponent);
}